import BaseService from './baseService';
import { socialAuthConfig, getFullUrl, getCallbackUrl } from '../config/socialAuth';

export interface SocialAuthResponse {
  success: boolean;
  message: string;
  flow_type?: 'signup' | 'login';
  redirect_to?: string;
  user: {
    id: number;
    email: string;
    full_name: string;
    is_social_user: boolean;
    provider: string;
    last_login?: string;
    created_at?: string;
  };
  tokens: {
    access: string;
    refresh: string;
  };
}

export interface SocialAuthUrlResponse {
  provider: string;
  auth_url: string;
  redirect_uri: string;
}

class SocialAuthService extends BaseService {
  /**
   * Obtém a URL de autorização para um provedor social
   */
  async getAuthUrl(provider: 'google' | 'facebook', action: 'signup' | 'login' = 'login'): Promise<SocialAuthUrlResponse> {
    try {
      let endpoint: string;
      
      if (provider === 'google') {
        endpoint = action === 'signup' 
          ? socialAuthConfig.endpoints.googleSignup
          : socialAuthConfig.endpoints.googleLogin;
      } else {
        endpoint = socialAuthConfig.endpoints.facebookAuthUrl;
      }
      
      const fullUrl = `${this.getApiUrl()}${endpoint}`;
      console.log(`DEBUG: Chamando URL: ${fullUrl}`);
      console.log(`DEBUG: Provider: ${provider}, Action: ${action}, Endpoint: ${endpoint}`);
      
      const response = await fetch(fullUrl);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`Erro ao obter URL de autorização para ${provider}:`, error);
      throw error;
    }
  }

  /**
   * Processa o callback de autenticação social
   */
  async processCallback(provider: 'google' | 'facebook', code: string, action: 'signup' | 'login' = 'login'): Promise<SocialAuthResponse> {
    try {
      let endpoint: string;
      
      if (provider === 'google') {
        // Usar a nova rota unificada de callback
        endpoint = socialAuthConfig.endpoints.googleCallback;
      } else {
        endpoint = socialAuthConfig.endpoints.callback;
      }
      
      const response = await fetch(`${this.getApiUrl()}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code,
          state: action // Enviar o tipo de ação como state
        })
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`Erro ao processar callback do ${provider} (${action}):`, error);
      throw error;
    }
  }

  /**
   * Inicia o fluxo de autenticação social
   */
  async initiateSocialAuth(provider: 'google' | 'facebook', action: 'signup' | 'login' = 'login'): Promise<void> {
    try {
      console.log(`DEBUG: Iniciando autenticação ${provider} com ação ${action}`);
      
      // 1. Obter URL de autorização
      const authData = await this.getAuthUrl(provider, action);
      
      console.log(`DEBUG: URL de autorização obtida:`, authData);
      
      // 2. Redirecionar para a URL de autorização
      window.location.href = authData.auth_url;
    } catch (error) {
      console.error(`Erro ao iniciar autenticação ${provider} (${action}):`, error);
      throw error;
    }
  }

  /**
   * Processa o código de autorização retornado pela rede social
   */
  async handleSocialCallback(provider: 'google' | 'facebook', code: string, action: 'signup' | 'login' = 'login'): Promise<SocialAuthResponse> {
    try {
      // 1. Processar callback no backend
      const response = await this.processCallback(provider, code, action);
      
      // 2. Salvar tokens no localStorage
      if (response.success && response.tokens) {
        localStorage.setItem('access_token', response.tokens.access);
        localStorage.setItem('refresh_token', response.tokens.refresh);
        localStorage.setItem('user', JSON.stringify(response.user));
      }
      
      return response;
    } catch (error) {
      console.error(`Erro ao processar callback do ${provider} (${action}):`, error);
      throw error;
    }
  }

  /**
   * Verifica se o usuário está autenticado via rede social
   */
  isSocialUser(): boolean {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return false;
      
      const user = JSON.parse(userStr);
      return user.is_social_user === true;
    } catch {
      return false;
    }
  }

  /**
   * Obtém informações do usuário social
   */
  getSocialUser() {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return null;
      
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }

  /**
   * Limpa dados de autenticação social
   */
  clearSocialAuth(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
  }

  /**
   * Verifica se a configuração está válida
   */
  isConfigValid(): boolean {
    return !!(socialAuthConfig.backendUrl && socialAuthConfig.frontendUrl);
  }

  /**
   * Obtém a URL de callback configurada
   */
  getCallbackUrl(): string {
    return getCallbackUrl();
  }
}

export const socialAuthService = new SocialAuthService();
